import {Audit} from "@core/model";
import {
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from "@mui/lab";
import {useSelector} from "react-redux";
import {Selectors} from "@core/store/selectors";
import {getUserText, translate, truncateSx} from "@util";
import dayjs from "dayjs";
import {format} from "@constants";
import {Link} from "react-router-dom";
import {
  AssetBuildingIcon,
  AssetIcon,
  AssetMachineIcon,
  AssetProductIcon,
  AssetProductionIcon,
  AssetVehicleIcon,
  AuditIcon,
  DocumentIcon,
  SensorMultivalueIcon,
  SensorScalarIcon,
  TicketIcon,
  TaskIcon,
  TagIcon,
  ArchiveIcon,
} from "@components/icons";
import {Typography} from "@mui/material";

const TimelineAudit = ({audit}: {audit: Audit}) => {
  const users = useSelector(Selectors.users) ?? [];

  const user = users.find(u => u.id === audit.userId);
  const userText = user ? getUserText(user) : "";

  const action = translate("pages.home.lastAudits.action." + audit.action);

  const createdAt = dayjs(audit.createdAt).format(format.datetime.short);

  let section;
  switch (audit.entity) {
    case "building":
    case "machine":
    case "asset":
    case "sensorscalar":
    case "sensormultivalue":
    case "production":
    case "product":
    case "vehicle":
      section = "assets";
      break;
    case "task":
      section = "tasks/day";
      break;
    case "ticket":
      section = "tickets";
      break;
    case "artifact":
      section = "artifacts";
      break;
    case "goods":
      section = "goods";
      break;
    case "document":
      section = "documents";
      break;
    case "tag":
      section = "tags";
      break;
    case "measure":
      section = "archive";
      break;
  }

  const text = audit.name ?? translate("entity." + audit.entity + ".title");

  const getEntityIcon = (entity: string) => {
    switch (entity) {
      case "ticket":
        return <TicketIcon fontSize="inherit" />;
      case "task":
        return <TaskIcon fontSize="inherit" />;
      case "asset":
        return <AssetIcon fontSize="inherit" />;
      case "building":
        return <AssetBuildingIcon fontSize="inherit" />;
      case "machine":
        return <AssetMachineIcon fontSize="inherit" />;
      case "sensorscalar":
        return <SensorScalarIcon fontSize="inherit" />;
      case "sensormultivalue":
        return <SensorMultivalueIcon fontSize="inherit" />;
      case "production":
        return <AssetProductionIcon fontSize="inherit" />;
      case "product":
        return <AssetProductIcon fontSize="inherit" />;
      case "vehicle":
        return <AssetVehicleIcon fontSize="inherit" />;
      case "document":
        return <DocumentIcon fontSize="inherit" />;
      case "tag":
        return <TagIcon fontSize="inherit" />;
      case "measure":
        return <ArchiveIcon fontSize="inherit" />;
      default:
        return <AuditIcon fontSize="inherit" />;
    }
  };

  return (
    <TimelineItem>
      <TimelineSeparator>
        <TimelineDot color="primary" variant="filled">
          {getEntityIcon(audit.entity)}
        </TimelineDot>
        <TimelineConnector />
      </TimelineSeparator>
      <TimelineContent
        sx={{
          flexDirection: "column",
          display: "flex",
          pb: 3,
          minWidth: 0,
        }}>
        <Typography
          color="textPrimary"
          variant="body2"
          sx={{
            ...truncateSx,
          }}>
          {userText} {translate("pages.home.lastAudits.has")} {action}{" "}
          {audit.action !== "delete" ? (
            <Link
              to={`/dashboard/${section}${audit.entityId ? `/${audit.entityId}` : ""}`}>
              {text}
            </Link>
          ) : (
            text
          )}
        </Typography>
        <Typography color="textSecondary" variant="caption" sx={{minWidth: 70}}>
          {createdAt}
        </Typography>
      </TimelineContent>
    </TimelineItem>
  );
};

export {TimelineAudit};
